export function getFirstDefinedValue(values) {
  const resolved_value = values.find((value) => value != null && value !== '') ?? '';
  return resolved_value;
}

export function joinMessageItems(items, fallback) {
  const joined_items = items.map((item) => String(item || '').trim()).filter(Boolean).join(', ') || fallback;
  return joined_items;
}

export function getQuestionTypeValue(question) {
  const question_type_value = getFirstDefinedValue([
    question.question_type?.id,
    question.question_type_id,
    question.question_type?.question_type,
    question.question_type?.type,
    question.type,
  ]);
  return question_type_value;
}

export function getQuestionOptionInputs(ctx, question) {
  const option_source = Array.isArray(question?.multi_select_choices)
    ? question.multi_select_choices
    : Array.isArray(question?.options)
      ? question.options
      : [];
  return option_source.map((choice) => ctx.extract_choice_label(choice));
}

export function getQuestionIdentity(question) {
  const question_identity = getFirstDefinedValue([
    question.question_id,
    question.id,
    question.question?.id,
    question.backend_id,
  ]) || null;
  return question_identity;
}

export function getQuestionDependants(question) {
  if (Array.isArray(question.dependent_questions)) {
    return question.dependent_questions;
  }

  if (Array.isArray(question.dependant_questions)) {
    return question.dependant_questions;
  }

  return [];
}

export function getSectionSource(item) {
  const section_source = item?.section ?? item ?? {};
  return section_source;
}

export function getMainSectionSource(item) {
  const main_section_source = item?.main_section ?? item ?? {};
  return main_section_source;
}

export function parseNormalizedErrorMessage(trimmed_message, fallback) {
  let normalized_message = trimmed_message;
  try {
    const parsed_message = JSON.parse(trimmed_message.replace(/'/g, '"'));
    if (Array.isArray(parsed_message)) {
      normalized_message = joinMessageItems(parsed_message, fallback);
    }
  } catch {
    normalized_message = trimmed_message;
  }
  return normalized_message;
}

export function getDependentQuestionFlags(question) {
  const dependent_flags = [
    question?.is_assigned_as_dependent,
    question?.is_dependent,
    question?.dependent_question_id,
    question?.dependent_question?.id,
    question?.main_question_id,
    question?.parent_question_id,
    question?.parent_question?.id,
    question?.trigger_condition,
  ];
  return dependent_flags;
}

export function buildFallbackQuestion(index, question) {
  const fallback_question = {
    id: `${Date.now()}-${index}`,
    text: String(question ?? `Question ${index + 1}`),
    description: '',
    type_label: '',
  };
  return fallback_question;
}

function resolveQuestionTextValue(question, index) {
  const question_text_value = getFirstDefinedValue([
    question.question_text,
    question.question,
    question.text,
    question.name,
    `Question ${index + 1}`,
  ]);
  return question_text_value;
}

export function resolveSectionQuestionId(question) {
  const section_question_id = getFirstDefinedValue([
    question.section_question_id,
    question.section_question?.id,
    question.form_section_question_id,
    question.section_question_mapping_id,
  ]) || null;
  return section_question_id;
}

export function resolveQuestionText(question, index) {
  const question_text = resolveQuestionTextValue(question, index);
  return question_text;
}

export function resolveQuestionTypeLabel(ctx, question) {
  const raw_type = getFirstDefinedValue([
    question.question_type?.question_type,
    question.question_type?.type,
    question.question_type,
    question.type,
  ]) || '';
  const type_label = ctx.format_question_type_label(raw_type);
  return type_label;
}

export function normalizeOptionInputs(option_inputs) {
  const normalized_option_inputs = option_inputs.length >= 2 ? option_inputs : ['', ''];
  return normalized_option_inputs;
}

function buildQuestionIdentifiers(question, index, question_id) {
  const question_identifiers = {
    id: question_id ?? `${Date.now()}-${index}`,
    backend_id: question_id ?? null,
    question_id: question_id,
    section_question_id: resolveSectionQuestionId(question),
  };
  return question_identifiers;
}

function buildQuestionContent(question, index) {
  const question_content = {
    text: resolveQuestionText(question, index),
    description: question.question_description ?? '',
    question_number: question.question_number ?? '',
  };
  return question_content;
}

function buildQuestionFlags(question) {
  const question_flags = {
    is_mandatory: Boolean(question.is_mandatory ?? true),
    is_unique: Boolean(question.is_unique),
    dependant_questions: getQuestionDependants(question),
    is_unassigning: false,
  };
  return question_flags;
}

function buildQuestionBase(question, index, question_id) {
  const question_base = {
    ...buildQuestionIdentifiers(question, index, question_id),
    ...buildQuestionContent(question, index),
    ...buildQuestionFlags(question),
  };
  return question_base;
}

function buildQuestionTypeState(ctx, question, question_type_value, option_inputs) {
  const question_type_state = {
    question_type_id: question_type_value,
    show_option_inputs: ctx.question_type_requires_options(question_type_value),
    option_inputs: normalizeOptionInputs(option_inputs),
    type_label: resolveQuestionTypeLabel(ctx, question),
  };
  return question_type_state;
}

export function buildNormalizedQuestion(ctx, question, index, question_id, question_type_value, option_inputs) {
  const normalized_question = {
    ...buildQuestionBase(question, index, question_id),
    ...buildQuestionTypeState(ctx, question, question_type_value, option_inputs),
  };
  return normalized_question;
}

function appendFlattenedSection(sections, section, flat_index) {
  const normalized_section = { ...section, original_index: flat_index };
  sections.push(normalized_section);
}

export function flattenMainSections(main_sections) {
  let flat_index = 0;
  const sections = [];
  main_sections.forEach((main_section) => {
    (Array.isArray(main_section.sections) ? main_section.sections : []).forEach((section) => {
      appendFlattenedSection(sections, section, flat_index);
      flat_index += 1;
    });
  });
  return sections;
}